import { AccountType } from "@common/constants/domains/accounting/accounts";
import {
    DraftAccount,
    DraftState,
    EditableAccountPatch,
    GenerateId,
    replaceAccount,
} from "./draftState";
import {
    nextSortOrderInScope,
    placeNewRowInDraftScope,
    ScopeInsertPosition,
} from "./draftSortOrder";

interface AddAccountInScopeParams {
    state: DraftState;
    entityId: number;
    type: AccountType;
    parentGroupId: string | null;
    // Where the new row lands within its scope; defaults to appending at the
    // bottom (the footer "Add account" behaviour).
    position?: ScopeInsertPosition;
    generateId: GenerateId;
    // The suggestion is computed once per gesture by the caller and written
    // verbatim onto the new row. Sequential fan-out inserts would otherwise each
    // re-derive against the previous business's just-added row and walk the
    // candidate past it, splitting a merged fan-out into separate rows.
    codeText: string | null;
}

export const addAccountInScope = ({
    state,
    entityId,
    type,
    parentGroupId,
    position = { kind: "append" },
    generateId,
    codeText,
}: AddAccountInScopeParams): { state: DraftState; accountId: string } => {
    const accountId = generateId();
    const baseAccount: Omit<DraftAccount, "sortOrder"> = {
        id: accountId,
        entityId,
        codeText,
        name: "",
        type,
        isDisabled: false,
        isManuallyCreated: true,
        parentGroupId,
    };

    if (position.kind === "append") {
        const newAccount: DraftAccount = {
            ...baseAccount,
            sortOrder: nextSortOrderInScope({
                accounts: state.accounts,
                groups: state.groups,
                entityId,
                type,
                parentGroupId,
            }),
        };
        return {
            state: { ...state, accounts: [...state.accounts, newAccount] },
            accountId,
        };
    }

    const newAccount: DraftAccount = {
        ...baseAccount,
        sortOrder: 0,
    };
    return {
        state: {
            ...state,
            ...placeNewRowInDraftScope({
                accounts: state.accounts,
                groups: state.groups,
                entityId,
                type,
                parentGroupId,
                position,
                placed: { id: accountId, kind: "account", name: "" },
                nextAccounts: [...state.accounts, newAccount],
                nextGroups: state.groups,
            }),
        },
        accountId,
    };
};

export const updateAccount = ({
    state,
    id,
    patch,
}: {
    state: DraftState;
    id: string;
    patch: EditableAccountPatch;
}): DraftState => ({
    ...state,
    accounts: replaceAccount(state.accounts, id, (account) => ({
        ...account,
        ...patch,
    })),
});

export const deleteAccount = ({
    state,
    id,
}: {
    state: DraftState;
    id: string;
}): DraftState => ({
    ...state,
    accounts: state.accounts.filter((account) => account.id !== id),
});

export const setAccountDisabled = ({
    state,
    id,
    isDisabled,
}: {
    state: DraftState;
    id: string;
    isDisabled: boolean;
}): DraftState => ({
    ...state,
    accounts: replaceAccount(state.accounts, id, (account) => ({
        ...account,
        isDisabled,
    })),
});
